import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import {
	Block,
	BLOCKS,
	Document as ContentfulDocument,
	Inline,
	INLINES,
} from '@contentful/rich-text-types';
import classNames from 'classnames';
import { GatsbyImage } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import React, { ReactNode, useMemo } from 'react';

import { isV2OrLater } from '../../util/deployment-version';
import { AnyLink } from '../any-link';
import { Callout } from '../callout';
import { Carousel } from '../carousel';
import {
	getMultipleDatasets,
	getSingleDataset,
	LineGraph,
	PieChart,
	StatisticsBlock,
	VerticalBarChart,
} from '../data-visualization';
import { DetailedList } from '../detailed-list';
import { EmbeddedHtml } from '../embedded-html';
import { ImageGrid } from '../image-grid';
import { LoginWrapper } from '../login-wrapper';
import { LogoGrid } from '../logo-grid';
import { ProtectedContent } from '../protected-content';
import { Quote } from '../quote';
import { RelatedContentBlock } from '../related-content';
import { SimpleList } from '../simple-list';
import { StatsBarArticleBlock } from '../stats-bar-article-block';
import { VideoPlayer } from '../video-player';
import {
	article as articleStyle,
	content as contentStyle,
	footer as footerStyle,
	header as headerStyle,
	heading as headingStyle,
	image as imageStyle,
	list as listStyle,
	noOverlap as noOverlapStyle,
	overlapBoth as overlapBothStyle,
	overlapLeftExceptTablet as overlapLeftExceptTabletStyle,
	overlapNone as overlapNoneStyle,
	paragraph as paragraphStyle,
	pdfThumbnail as pdfThumbnailStyle,
	summary as summaryStyle,
} from './style.module.css';
import { layoutForImageGrid } from './util';

const createRenderRichTextOptions = (fieldsKey?: (target: any) => any): Options => ({
	renderNode: {
		[BLOCKS.EMBEDDED_ENTRY]: (node) => <ArticleEntry contentfulNode={node} fieldsKey={fieldsKey} />,
		[BLOCKS.EMBEDDED_ASSET]: (node) => <ArticleAsset contentfulNode={node} />,
		[INLINES.EMBEDDED_ENTRY]: (node) => (
			<ArticleEntry contentfulNode={node} fieldsKey={fieldsKey} />
		),
		[INLINES.ENTRY_HYPERLINK]: () => <ArticleHyperlink />,
		[INLINES.ASSET_HYPERLINK]: () => <ArticleHyperlink />,
		[BLOCKS.PARAGRAPH]: (node, children) => (
			<p className={classNames(overlapNoneStyle, paragraphStyle)}>{children}</p>
		),
		[BLOCKS.QUOTE]: (node, children) => {
			const quoteValue = (node as any).content?.[0]?.content?.[0]?.value;
			if (!quoteValue) return <></>;
			return (
				<div className={overlapNoneStyle}>
					<Quote quote={quoteValue} />
				</div>
			);
		},

		[BLOCKS.OL_LIST]: (node, children) => (
			<div className={overlapNoneStyle}>
				<ol className={listStyle}>{children}</ol>
			</div>
		),
		[BLOCKS.UL_LIST]: (node, children) => (
			<div className={overlapNoneStyle}>
				<ul className={listStyle}>{children}</ul>
			</div>
		),
		[BLOCKS.HEADING_1]: (node, children) => (
			<h1 className={classNames(overlapNoneStyle, headingStyle)}>{children}</h1>
		),
		[BLOCKS.HEADING_2]: (node, children) => (
			<h2 className={classNames(overlapNoneStyle, headingStyle)}>{children}</h2>
		),
		[BLOCKS.HEADING_3]: (node, children) => (
			<h3 className={classNames(overlapNoneStyle, headingStyle)}>{children}</h3>
		),
		[BLOCKS.HEADING_4]: (node, children) => (
			<h4 className={classNames(overlapNoneStyle, headingStyle)}>{children}</h4>
		),
		[BLOCKS.HEADING_5]: (node, children) => (
			<h5 className={classNames(overlapNoneStyle, headingStyle)}>{children}</h5>
		),
		[BLOCKS.HEADING_6]: (node, children) => (
			<h6 className={classNames(overlapNoneStyle, headingStyle)}>{children}</h6>
		),
	},
});

export interface ArticleContentProps {
	content: { raw: any; references: any } | ContentfulDocument | undefined;
	summary?: string;
	renderTitle?(): ReactNode;
	renderNextArticle?(): ReactNode;
	fieldsKey?(target: any): any;
	noOverlap?: boolean;
}

export const ArticleContent = ({
	content,
	summary,
	renderTitle,
	renderNextArticle,
	fieldsKey,
	noOverlap = false,
}: ArticleContentProps): JSX.Element => {
	const renderedRichText = useMemo(() => {
		if (!content) return <></>;
		try {
			if ((content as any).raw) {
				// Produced by gatsby-source-contentful
				return renderRichText(content as any, createRenderRichTextOptions(fieldsKey));
			} else {
				// Direct contentful output through graphweaver
				return documentToReactComponents(
					content as ContentfulDocument,
					createRenderRichTextOptions(fieldsKey)
				);
			}
		} catch (error) {
			return (
				<div className={overlapNoneStyle}>
					<h1>Error while parsing article</h1>
					<p>{String(error)}</p>
					<code>{JSON.stringify(content)}</code>
				</div>
			);
		}
	}, [content]);

	return (
		<div className={classNames(noOverlap && noOverlapStyle, articleStyle)}>
			<div className={headerStyle}>{!!renderTitle && renderTitle()}</div>
			{!!summary && <div className={summaryStyle} dangerouslySetInnerHTML={{ __html: summary }} />}
			{!!content && <div className={contentStyle}>{renderedRichText}</div>}
			<div className={footerStyle}>{!!renderNextArticle && renderNextArticle()}</div>
		</div>
	);
};

export interface ArticleAssetProps {
	contentfulNode: Inline | Block;
}

export const ArticleAsset = ({ contentfulNode }: ArticleAssetProps): JSX.Element => {
	const { data } = contentfulNode;

	if (!data) return <></>;

	// @todo use the content types or file type groups for every one
	if (data.target.gatsbyImageData)
		return (
			<GatsbyImage
				className={imageStyle}
				image={data.target.gatsbyImageData}
				alt={data.target.title ?? 'Image'}
			/>
		);

	if (data.target.file.contentType.startsWith('video'))
		return (
			<VideoPlayer videoOnly video={{ url: data.target.file.url, title: data.target.title }} />
		);

	if (data.target.file.contentType === 'application/pdf') {
		return (
			<a href={data.target.file.url} className={pdfThumbnailStyle}>
				<img src={data.target.localFile.graphicsMagick} alt={data.target.title} />
				<p>Download {data.target.title}</p>
			</a>
		);
	}

	console.warn(`Content type not implemented: ${data.target.file.contentType}`);
	return <></>;
};

export interface ArticleEntryProps {
	contentfulNode: Inline | Block;
	fieldsKey?(target: any): any;
}

export const ArticleEntry = ({
	contentfulNode,
	fieldsKey = (target) => target,
}: ArticleEntryProps): JSX.Element => {
	if (!contentfulNode.data.target) return <></>;

	const {
		type,
		quote,
		quoteAuthor,
		quoteAuthorTitle,
		quoteAuthorImage,
		listSimple,
		listDetailed,
		listCallout,
		imageCarousel,
		imageGalleryImages,
		imageGalleryDisplay,
		statistics,
		logoGrid,
		chartOrGraph,
		relatedContentLinks,
		relatedContentTerms,
		relatedContentMaxItems,
		htmlEmbed,
	} = fieldsKey(contentfulNode.data.target) as any; // @todo find the relevant type - contentful ResolvedField?

	// @todo about fieldsKey:
	// Graphweaver uses contentful's format directly and so puts the fields on .fields, whereas the
	// gatsby contentful source takes them out and flattens them.
	// So, while we're still using both, we'll check if these entries have a .fields
	// and if so, use that.

	switch (type) {
		case 'Quote':
			// @todo images broken on protected content articles as they are missing gatsbyImageData
			if (!quote || !quoteAuthor || !quoteAuthorTitle) return <></>;
			return (
				<div className={overlapNoneStyle}>
					<Quote
						quote={quote?.quote ?? quote}
						author={quoteAuthor}
						authorTitle={quoteAuthorTitle}
						renderAuthorImage={() =>
							!!quoteAuthorImage?.gatsbyImageData && (
								<GatsbyImage
									image={quoteAuthorImage.gatsbyImageData}
									alt={quoteAuthorImage.title}
								/>
							)
						}
					/>
				</div>
			);

		case 'Simple List': {
			if (!Array.isArray(listSimple)) return <></>;
			const items = listSimple.flatMap((item) => (item ? fieldsKey(item) : []));
			return (
				<div className={overlapNoneStyle}>
					<SimpleList items={items} />
				</div>
			);
		}

		case 'Detailed List': {
			if (!Array.isArray(listDetailed)) return <></>;
			const items = listDetailed.flatMap((item) => (item ? fieldsKey(item) : []));
			return (
				<div className={overlapNoneStyle}>
					<DetailedList items={items} />
				</div>
			);
		}

		case 'Callout List': {
			if (!Array.isArray(listCallout)) return <></>;
			const items = listCallout.flatMap((item) => (item ? fieldsKey(item) : []));
			return (
				<div className={overlapLeftExceptTabletStyle}>
					<Callout items={items} />
				</div>
			);
		}

		case 'Image Carousel': {
			// Filter out empty of invalid images from the array
			// @todo fix the type here
			// @todo fix mismatched file url field
			const items = imageCarousel
				?.map((imageCarouselItem: any) => fieldsKey(imageCarouselItem))
				.flatMap((imageCarouselItem: any) =>
					imageCarouselItem && imageCarouselItem.title
						? imageCarouselItem.url
							? { title: imageCarouselItem.title, url: imageCarouselItem.url }
							: imageCarouselItem.fileUrl
							? { title: imageCarouselItem.title, url: imageCarouselItem.fileUrl }
							: []
						: []
				);

			return (
				<div className={overlapBothStyle}>
					<Carousel items={items} />
				</div>
			);
		}

		case 'Image Gallery': {
			// @todo won't work on protected articles because of missing gatsbyImageData
			// If images is not a array, return an empty fragment
			if (!Array.isArray(imageGalleryImages)) return <></>;

			// Remove undefined or null images from the array
			const items = [...imageGalleryImages]
				.map((image) => fieldsKey(image))
				.flatMap((image) => (image && image.gatsbyImageData && image.title ? image : []));

			// Convert to layout
			const layout = layoutForImageGrid(imageGalleryDisplay);

			return (
				<div className={overlapBothStyle}>
					<ImageGrid
						layout={layout}
						items={items}
						renderItem={({ gatsbyImageData, title }) =>
							!!gatsbyImageData && !!title && <GatsbyImage image={gatsbyImageData} alt={title} />
						}
					/>
				</div>
			);
		}

		case 'Logo Grid': {
			// @todo won't work on protected articles because of missing gatsbyImageData
			// If images is not a array, return an empty fragment
			if (!Array.isArray(logoGrid)) return <></>;

			// @todo: inject index (for use as key) into renderItem done in LogoGrid but has no effect
			return (
				<LogoGrid
					items={logoGrid}
					renderItem={({ image, slug, title }, _index) =>
						!!image &&
						!!title && (
							<AnyLink url={slug}>
								<img src={image.file.url} alt={title} />
							</AnyLink>
						)
					}
				/>
			);
		}

		case 'Statistics Bar': {
			if (!Array.isArray(statistics)) return <></>;
			const items = statistics.flatMap((item) => (item ? fieldsKey(item) : []));
			return (
				<div className={overlapBothStyle}>
					<StatsBarArticleBlock items={items} />
				</div>
			);
		}

		case 'Related Content': {
			const links = Array.isArray(relatedContentLinks)
				? relatedContentLinks.flatMap((link) => (link ? link : []))
				: [];
			// @todo fix the type here
			const tags = relatedContentTerms
				? relatedContentTerms.flatMap((tag: string) => (tag ? tag : []))
				: [];
			return (
				<RelatedContentBlock maxItems={relatedContentMaxItems ?? 5} links={links} tags={tags} />
			);
		}

		case 'Chart or Graph': {
			if (!chartOrGraph) return <></>;

			const { datasets } = chartOrGraph;

			if (Array.isArray(datasets)) {
				const data = datasets.flatMap((set) => set ?? []);
				data.forEach((set) => {
					const { x, y } = set?.dataset ?? {};
					if (!x || x.values?.length === 0 || !y || y.values?.length === 0) return <></>;
				});

				switch (chartOrGraph.chartType) {
					case 'Line Graph': {
						return (
							<div className={overlapNoneStyle}>
								<LineGraph
									displayTitle={chartOrGraph.displayTitle ?? ''}
									subtitle={chartOrGraph?.subtitle ?? ''}
									dataItems={getMultipleDatasets(chartOrGraph)}
								/>
							</div>
						);
					}

					case 'Pie Chart': {
						return (
							<div className={overlapNoneStyle}>
								<PieChart
									displayTitle={chartOrGraph?.displayTitle ?? ''}
									subtitle={chartOrGraph?.subtitle ?? ''}
									dataItems={getSingleDataset(chartOrGraph)}
								/>
							</div>
						);
					}

					case 'Bar Chart': {
						return (
							<div className={overlapNoneStyle}>
								<VerticalBarChart
									displayTitle={chartOrGraph?.displayTitle ?? ''}
									subtitle={chartOrGraph?.subtitle ?? ''}
									dataItems={getMultipleDatasets(chartOrGraph)}
								/>
							</div>
						);
					}
					case 'Statistics Block': {
						return (
							<div className={overlapNoneStyle}>
								<StatisticsBlock
									displayTitle={chartOrGraph?.displayTitle ?? ''}
									subtitle={chartOrGraph?.subtitle ?? ''}
									dataItems={getSingleDataset(chartOrGraph)}
								/>
							</div>
						);
					}
					default:
						console.warn(`Data visualization option not implemented: ${chartOrGraph.chartType}`);
						return <code>{JSON.stringify(chartOrGraph)}</code>;
				}
			} else return <></>;
		}

		case 'HTML Embed': {
			return <EmbeddedHtml content={htmlEmbed?.htmlEmbed ?? htmlEmbed} />;
		}

		// Testing protected content here
		case 'Protected Content': {
			if (!isV2OrLater()) return <></>;

			const contentfulProtectedContentId = contentfulNode.data.target?.sys?.id;

			let groupsAllowedToView;

			if (contentfulNode.data.target.fields) {
				groupsAllowedToView = (contentfulNode.data.target as any)?.fields?.groupsAllowedToView;
			} else {
				groupsAllowedToView = (
					contentfulNode.data.target as Partial<Queries.ContentfulProtectedContent>
				).groupsAllowedToView;
			}

			if (!contentfulProtectedContentId || !groupsAllowedToView)
				return (
					<p>
						Missing ID or groups{JSON.stringify(contentfulNode.data.target.groupsAllowedToView)}
					</p>
				);

			const filteredGroupsAllowedToView = groupsAllowedToView
				? [...groupsAllowedToView].flatMap((group) => group ?? [])
				: undefined;

			return (
				<div className={overlapBothStyle}>
					<LoginWrapper allowedGroups={filteredGroupsAllowedToView}>
						<ProtectedContent protectedContentId={contentfulProtectedContentId} />
					</LoginWrapper>
				</div>
			);
		}

		default:
			console.warn(`Display option not implemented: ${type}`);
			return <></>;
	}
};

export const ArticleHyperlink = (): JSX.Element => {
	console.warn('Embed not implemented: hyperlink');
	return <></>;
};
